import * as Sentry from '@sentry/react';
import { useAuthStore } from '@store/auth';

/**
 * Sentry init — main.tsx içinde <App /> öncesi çağır.
 * VITE_SENTRY_DSN tanımlı değilse no-op.
 *
 * Defansif tasarım:
 *  - DSN yok → sessizce skip et
 *  - Sentry init exception fırlatırsa app'i kırma
 *  - Transport hatasi (kullanici AV/proxy SSL MITM yapiyorsa
 *    `ERR_CERT_AUTHORITY_INVALID` gelir) sessize al
 *  - beforeSend ile kendi `@sentry` modulunden gelen hatalari filtrele
 *    (bunlar sonsuz dongu yapabilir)
 */
let initialized = false;

export function initSentry(): boolean {
  if (initialized) return true;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info('[sentry] VITE_SENTRY_DSN yok, hata izleme devre dışı.');
    }
    return false;
  }

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      // Production build hash'i ile release etiketle — Sentry'de issue grouping iyileşir
      release: import.meta.env.VITE_APP_VERSION || undefined,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Sentry'nin kendi transport hatalarini filtrele (sonsuz dongu olmasin)
      beforeSend(event, hint) {
        const error = hint.originalException;
        if (error instanceof Error) {
          const msg = error.message || '';
          // Kullanicinin AV/proxy SSL inspection yapiyor — gercek bir hata degil
          if (
            msg.includes('ERR_CERT_AUTHORITY_INVALID') ||
            msg.includes('NetworkError') ||
            msg.includes('Failed to fetch') &&
              event.tags?.transport
          ) {
            return null;
          }
          // Sentry SDK'nin kendi hatalarini at — sonsuz dongu olabilir
          const stack = error.stack || '';
          if (stack.includes('@sentry/') || stack.includes('sentry-cdn')) {
            return null;
          }
        }
        return event;
      },
      // ResizeObserver loop limit gibi yaygin gurultu hatalarini ignore et
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
        // Cloudflare email obfuscation, browser extension hatalari
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
      ],
    });

    // me değiştikçe Sentry user/tenant tag'lerini güncelle.
    useAuthStore.subscribe((state) => {
      if (state.me) {
        Sentry.setUser({ id: state.me.userId, email: state.me.email });
        Sentry.setTag('tenantId', state.me.tenantId);
        Sentry.setTag('plan', state.me.plan);
      } else {
        Sentry.setUser(null);
      }
    });

    initialized = true;
    return true;
  } catch (err) {
    // Sentry init'inde hata olursa app'i kirmak istemiyoruz
    // eslint-disable-next-line no-console
    console.warn('[sentry] init başarısız, hata izleme devre dışı:', err);
    return false;
  }
}

export { Sentry };
